import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { PopoverDirection } from './types';

import { PopoverContext } from './context';
import { CARET_HEIGHT } from './utils';
import './Popover.css';

const VIEWPORT_PADDING = 8;
const POPOVER_TRIGGER_GAP = 2;

type Props = {
  children: ReactNode;
  /** Where the content opens relative to the trigger. @default 'bottom-start' */
  direction?: PopoverDirection;
  /** Controlled open state. */
  open?: boolean;
  /** Fires when the popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
};

export const Popover = memo(
  ({ children, direction = 'bottom-start', open, onOpenChange }: Props) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = open ?? uncontrolledOpen;

    const setOpen = useCallback(
      (next: boolean) => {
        if (open === undefined) {
          setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
      },
      [open, onOpenChange],
    );

    const { context, floatingStyles, refs } = useFloating<HTMLElement>({
      middleware: [
        offset(POPOVER_TRIGGER_GAP + CARET_HEIGHT),
        flip(),
        shift({ padding: VIEWPORT_PADDING }),
        size({
          padding: VIEWPORT_PADDING,
          apply({ availableHeight, elements }) {
            /*
             * The system has no token for a dropdown's maximum height, so the
             * cap comes from the space the viewport actually offers rather than
             * a hardcoded value.
             */
            elements.floating.style.setProperty(
              '--_available-height',
              `${availableHeight}px`,
            );
          },
        }),
      ],
      open: isOpen,
      placement: direction,
      whileElementsMounted: autoUpdate,
      onOpenChange: setOpen,
    });

    // Prefer click over mousedown so the press can focus the trigger.
    const click = useClick(context, { toggle: true });
    // A consumer can keep the popover "open" while swapping its content for
    // something else entirely (e.g. a modal rendered as a sibling instead of
    // `PopoverContent`) — see `ConditionPanel`. With no floating node mounted,
    // there is nothing for a press to be "outside" of, so every press
    // anywhere would otherwise register as an outside press and close the
    // popover out from under that other content. `escapeKey` has the same
    // problem but takes no predicate, so it is disabled here and reimplemented
    // below with the same guard.
    const dismiss = useDismiss(context, {
      escapeKey: false,
      outsidePress: () => refs.floating.current !== null,
    });
    const role = useRole(context, { role: 'dialog' });

    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || refs.floating.current === null) return;
        setOpen(false);
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, refs.floating, setOpen]);

    const { getFloatingProps, getReferenceProps } = useInteractions([
      click,
      dismiss,
      role,
    ]);

    const value = useMemo(
      () => ({
        context,
        floatingStyles,
        getFloatingProps,
        getReferenceProps,
        isOpen,
        setFloating: refs.setFloating,
        setReference: refs.setReference,
      }),
      [
        context,
        floatingStyles,
        getFloatingProps,
        getReferenceProps,
        isOpen,
        refs.setFloating,
        refs.setReference,
      ],
    );

    return (
      <PopoverContext.Provider value={value}>
        {children}
      </PopoverContext.Provider>
    );
  },
);

Popover.displayName = 'Popover';
